/*
 * Copyright (c) 2022-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { OktaAuth } from '@okta/okta-auth-js';
import omit from 'lodash/omit';

import {
  WebAuthNAuthenticationHandler,
  WebAuthNAutofillUICredentials,
  WebAuthNChallengeDataWithUserVerification,
  WebAuthNEnrollmentHandler,
} from '../types';
import { loc } from './locUtil';

export const binToStr = (bin: ArrayBuffer): string => btoa(
  new Uint8Array(bin).reduce((s, byte) => s + String.fromCharCode(byte), ''),
);

export const base64UrlSafeToBase64 = (str: string) => str.replace(new RegExp('_', 'g'), '/').replace(new RegExp('-', 'g'), '+');

export const strToBin = (str: string) => (
  Uint8Array.from(atob(base64UrlSafeToBase64(str)), (c) => c.charCodeAt(0))
);

export const isCredentialsApiAvailable = (): boolean => !!(navigator && navigator.credentials);
export const isCredentialsGetApiAvailable = (): boolean => !!(
  isCredentialsApiAvailable() && navigator.credentials.get
);
export const isCredentialsCreateApiAvailable = (): boolean => !!(
  isCredentialsApiAvailable() && navigator.credentials.create
);

// eslint-disable-next-line compat/compat, no-undef
export const isConditionalMediationAvailable = () => typeof PublicKeyCredential !== 'undefined'
  // eslint-disable-next-line compat/compat, no-undef
  && typeof PublicKeyCredential.isConditionalMediationAvailable !== 'undefined';

// checks if the browser supports passkey autofill by making sure it supports conditional mediation
// https://passkeys.dev/docs/reference/terms/#autofill-ui
export const isPasskeyAutofillAvailable = async () => {
  let isAvailable = false;
  if (isCredentialsGetApiAvailable() && isConditionalMediationAvailable()) {
    // eslint-disable-next-line compat/compat, no-undef
    isAvailable = await PublicKeyCredential.isConditionalMediationAvailable();
  }
  return isAvailable;
};

export const isGetPasskeyAvailable = () => isCredentialsGetApiAvailable() && typeof PublicKeyCredential !== 'undefined';

export const isRelyingPartyIdMismatchError = (error: unknown): boolean => (
  (error as DOMException)?.name === 'SecurityError'
  && (error as DOMException)?.code === 18
);

/**
 * Uses the Web Authentication API to generate credentials for enrolling
 * a user into the WebAuthN flow
 *
 * Used by {@link WebAuthNSubmitButton.tsx} Renderer to initiate the WebAuthN Enrollment flow.
 */
export const webAuthNEnrollmentHandler: WebAuthNEnrollmentHandler = async (transaction) => {
  // @ts-ignore OKTA-499928 authenticatorEnrollments missing from rawIdxState
  const { nextStep, rawIdxState: { authenticatorEnrollments } } = transaction;
  // NextStep is guranteed to be available here
  if (!nextStep) {
    throw new Error('transaction missing nextStep');
  }

  const { relatesTo } = nextStep;
  const activationData = relatesTo?.value?.contextualData?.activationData;

  // Generates a CredentialCreationOptions Object for the Web Auth API to
  // generate a PublicKeyCredential instance for use by IDX to enroll the user/device
  const options = (
    activationData && authenticatorEnrollments?.value
  ) && OktaAuth.webauthn.buildCredentialCreationOptions(
    activationData,
    authenticatorEnrollments.value,
  );

  let result: Credential | null;
  try {
    // Causes a browser prompt enabling the user to select the desired device to
    // enroll in this flow. Generates a Object that contains ClientData (origin, challenge)
    // and attestation (arraybuffer containing authenticator data)
    result = await navigator.credentials.create(options);
  } catch (error) {
    if (isRelyingPartyIdMismatchError(error)) {
      throw new Error(loc('signin.passkeys.error.SecurityError', 'login'));
    }
    throw error;
  }

  // Extracts clientData, attestation, and transports from the credential response
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...credentials } = OktaAuth.webauthn.getAttestation(
    result as PublicKeyCredential,
  );

  return { credentials };
};

/**
 * Uses the Web Authentication API to retrieve credentials for a client to authenticate
 *
 * Used by {@link WebAuthNSubmitButton.tsx} Renderer to initiate the WebAuthN Authentication flow.
 */
export const webAuthNAuthenticationHandler: WebAuthNAuthenticationHandler = async (transaction) => {
  // @ts-ignore OKTA-499928 authenticatorEnrollments missing from rawIdxState
  const { nextStep, rawIdxState: { authenticatorEnrollments } } = transaction;
  // NextStep is guranteed to be available here
  if (!nextStep) {
    throw new Error('transaction missing nextStep');
  }

  const { relatesTo } = nextStep;
  const challengeData = relatesTo?.value?.contextualData?.challengeData;

  // Generates a CredentialRequestOptions Object for the Web Auth API to
  // generate a PublicKeyCredential instance for use by IDX verify the user
  const options = (
    challengeData && authenticatorEnrollments?.value
  ) && OktaAuth.webauthn.buildCredentialRequestOptions(
    challengeData,
    authenticatorEnrollments.value,
  );

  let credentials: PublicKeyCredential;
  try {
    // Triggers a browser prompt allowing the user to provide consent based on the options
    // passed to this method to collect their credentials.
    credentials = await navigator.credentials.get(options) as PublicKeyCredential;
  } catch (error) {
    if (isRelyingPartyIdMismatchError(error)) {
      throw new Error(loc('signin.passkeys.error.rpIdMismatch', 'login', [challengeData?.rpId]));
    }
    throw error;
  }

  // Extracts the key properties from the credentials object and returns.
  // for some reason generic remediator does not allow/expect id field and fails when passed
  return { credentials: omit(OktaAuth.webauthn.getAssertion(credentials), ['id']) };
};

const challengeDataToCredentialRequestOptions = (
  challengeData: WebAuthNChallengeDataWithUserVerification,
): PublicKeyCredentialRequestOptions => ({
  ...challengeData,
  challenge: strToBin(challengeData.challenge),
});

function isAuthenticatorAssertionResponse(
  response: AuthenticatorAssertionResponse | AuthenticatorResponse,
): response is AuthenticatorAssertionResponse {
  return (
    (response as AuthenticatorAssertionResponse).userHandle
    instanceof ArrayBuffer
  );
}

const createPayloadFromCredentials = (
  credential: PublicKeyCredential,
): WebAuthNAutofillUICredentials | undefined => {
  if (credential && isAuthenticatorAssertionResponse(credential.response)) {
    return {
      clientData: binToStr(credential.response.clientDataJSON),
      authenticatorData: binToStr(credential.response.authenticatorData),
      signatureData: binToStr(credential.response.signature),
      userHandle: binToStr(credential.response.userHandle as ArrayBuffer),
    } as WebAuthNAutofillUICredentials;
  }
  return undefined;
};

export const webAuthNAutofillActionHandler = async (
  challengeData: WebAuthNChallengeDataWithUserVerification,
  abortController: AbortController | undefined,
): Promise<WebAuthNAutofillUICredentials | undefined> => {
  // if the browser doesn't support Passkey autofill and AbortController,
  // no action needs to be taken as there are other steps the user can take to proceed
  const supportsPasskeyAutofill = await isPasskeyAutofillAvailable();
  const supportsAbortController = typeof AbortController !== 'undefined';
  if (supportsPasskeyAutofill && supportsAbortController && abortController) {
    const credential = (await navigator.credentials.get({
      // @ts-ignore
      mediation: 'conditional',
      publicKey: challengeDataToCredentialRequestOptions(challengeData),
      signal: abortController.signal,
    })) as PublicKeyCredential;

    return createPayloadFromCredentials(credential);
  }
  return undefined;
};

export const webAuthNModalActionHandler = async (
  challengeData: WebAuthNChallengeDataWithUserVerification,
  abortController: AbortController | undefined,
): Promise<WebAuthNAutofillUICredentials | undefined> => {
  // if the browser doesn't support Passkey and AbortController,
  // no action needs to be taken as there are other steps the user can take to proceed
  const supportsAbortController = typeof AbortController !== 'undefined';
  if (!(isGetPasskeyAvailable() && supportsAbortController && abortController)) {
    const notSupportedError = new Error('Browser does not support Passkeys.');
    notSupportedError.name = 'NotSupportedError';
    throw notSupportedError;
  }
  const credential = (await navigator.credentials.get({
    mediation: 'optional',
    publicKey: challengeDataToCredentialRequestOptions(challengeData),
    signal: abortController.signal,
  })) as PublicKeyCredential;

  return createPayloadFromCredentials(credential);
};
